import React from 'react';
import { Smile, Trash2, File, Download } from 'lucide-react';
import { formatRelative } from '../../utils/dateUtils';

// Parse message content for links and basic formatting
const parseMessageContent = (content) => {
  if (!content) return null;
  
  // Regex patterns
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const boldRegex = /\*\*([^*]+)\*\*/g;
  const italicRegex = /\*([^*]+)\*/g;
  
  // First, handle markdown links [text](url)
  let processed = content.replace(markdownLinkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-300 hover:text-cyan-100 underline">$1</a>');
  
  // Then handle plain URLs (but not ones already in href)
  processed = processed.replace(/(?<!href=")(https?:\/\/[^\s<"]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-cyan-300 hover:text-cyan-100 underline break-all">$1</a>');
  
  // Handle bold
  processed = processed.replace(boldRegex, '<strong>$1</strong>');
  
  // Handle italics (but not ones inside URLs)
  processed = processed.replace(/(?<![\/:])\*([^*\n]+)\*(?![\/])/g, '<em>$1</em>');
  
  return processed;
};

const MessageList = ({ messages, currentUser, onReaction, onDelete }) => {
  if (!currentUser) {
    return <div className="text-slate-400 text-center py-4">Loading...</div>;
  }

  const groupReactions = (reactions) => {
    const grouped = {};
    reactions.forEach(reaction => {
      if (!grouped[reaction.emoji]) {
        grouped[reaction.emoji] = [];
      }
      grouped[reaction.emoji].push(reaction);
    });
    return grouped;
  };

  const getDisplayName = (message) => {
    return message.display_name || message.username;
  };

  const getInitial = (message) => {
    const name = message.display_name || message.username || 'U';
    return name.charAt(0).toUpperCase();
  };

  return (
    <>
      {messages.map((message) => {
        const isOwnMessage = message.user_id === currentUser.id;
        const groupedReactions = groupReactions(message.reactions || []);

        return (
          <div
            key={message.id}
            className={`group flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''}`}
          >
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden">
              {message.avatar_url ? (
                <img
                  src={message.avatar_url}
                  alt={getDisplayName(message)}
                  className="w-full h-full object-cover ring-2 ring-cyan-500/30 rounded-full"
                  onError={(e) => {
                    e.target.parentElement.innerHTML = `<div class="w-full h-full rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold shadow-lg shadow-cyan-500/30">${getInitial(message)}</div>`;
                  }}
                />
              ) : (
                <div className="w-full h-full rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold shadow-lg shadow-cyan-500/30">
                  {getInitial(message)}
                </div>
              )}
            </div>

            {/* Message Content */}
            <div className={`flex-1 max-w-2xl ${isOwnMessage ? 'flex flex-col items-end' : ''}`}>
              <div className={`flex items-baseline gap-2 mb-1 ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
                <span className="font-semibold text-white">
                  {getDisplayName(message)}
                </span>
                <span className="text-xs text-slate-500">
                  {formatRelative(message.created_at)}
                </span>
                {message.is_edited && (
                  <span className="text-xs text-slate-600">(edited)</span>
                )}
              </div>

              <div
                className={`rounded-2xl p-4 ${
                  isOwnMessage
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/20'
                    : 'bg-slate-800/50 text-slate-200 border border-cyan-500/20'
                }`}
              >
                <div 
                  className="whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: parseMessageContent(message.content) }}
                />

                {/* Attachments */}
                {message.attachments && message.attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {message.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className={`flex items-center gap-2 p-2 rounded-lg ${
                          isOwnMessage ? 'bg-white/10' : 'bg-slate-700/50'
                        }`}
                      >
                        <File size={16} className="text-cyan-400" />
                        <span className="text-sm truncate flex-1">
                          {attachment.filename}
                        </span>
                        <Download size={14} className="text-cyan-400 cursor-pointer hover:text-white" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reactions */}
              {Object.keys(groupedReactions).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {Object.entries(groupedReactions).map(([emoji, reactions]) => (
                    <button
                      key={emoji}
                      onClick={() => onReaction(message.id, emoji)}
                      className="flex items-center gap-1 px-2.5 py-1 bg-slate-800/50 hover:bg-cyan-500/20 rounded-full text-sm transition border border-cyan-500/20 hover:border-cyan-500/40"
                      title={reactions.map(r => r.username).join(', ')}
                    >
                      <span>{emoji}</span>
                      <span className="text-xs text-cyan-300">
                        {reactions.length}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Message Actions */}
              <div className={`flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
                <button
                  onClick={() => onReaction(message.id, '👍')}
                  className="p-1.5 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition"
                  title="Add Reaction"
                >
                  <Smile size={16} />
                </button>
                {isOwnMessage && (
                  <button
                    onClick={() => onDelete(message.id)}
                    className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition"
                    title="Delete Message"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default MessageList;
